/**
 * Typography Example Page
 *
 * Visual demonstration of all typography components and their proper usage.
 * Use this as a reference when implementing new features.
 */

import React from 'react';
import { Card, CardBody, CardHeader, Divider } from '@heroui/react';
import {
  Heading,
  Text,
  Label,
  Link,
  Code,
  Blockquote,
  Title,
  Subtitle,
  SectionTitle,
  Body,
  SecondaryText,
  Caption,
} from './Typography';

const TypographyExample = () => {
  return (
    <main className="container mx-auto px-4 py-8 max-w-4xl">
      <header className="mb-8">
        <Title>Typography System</Title>
        <SecondaryText className="mt-2">
          ClipKite design system for consistent, accessible text across the platform
        </SecondaryText>
      </header>

      {/* Headings */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Headings</Subtitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Caption className="mb-2">Heading Level 1 (Title)</Caption>
            <Heading level={1}>The quick brown fox</Heading>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Heading Level 2 (Subtitle)</Caption>
            <Heading level={2}>The quick brown fox</Heading>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Heading Level 3 (Section Title)</Caption>
            <Heading level={3}>The quick brown fox</Heading>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Heading Level 4</Caption>
            <Heading level={4}>The quick brown fox</Heading>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Heading Level 5</Caption>
            <Heading level={5}>The quick brown fox</Heading>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Heading Level 6</Caption>
            <Heading level={6}>The quick brown fox</Heading>
          </div>
        </CardBody>
      </Card>

      {/* Body Text */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Body Text Variants</Subtitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Caption className="mb-2">Primary Body (highest contrast)</Caption>
            <Body>
              This is the primary body text that users will read most frequently.
              It has the highest contrast for maximum readability.
            </Body>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Secondary Text (medium contrast)</Caption>
            <SecondaryText>
              This is secondary text used for supporting information that is less
              important than the main content but still needs to be readable.
            </SecondaryText>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Tertiary Text (lower contrast)</Caption>
            <Text variant="tertiary">
              This is tertiary text used for metadata and less critical information.
            </Text>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Small Text</Caption>
            <Text variant="small">
              This is small text used for fine print and details.
            </Text>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Caption (smallest)</Caption>
            <Caption>
              Posted 2 hours ago • 1,234 views • Colorado
            </Caption>
          </div>
        </CardBody>
      </Card>

      {/* Font Weights */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Font Weights</Subtitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Text weight="normal">Normal weight (400) - The quick brown fox</Text>
          <Text weight="medium">Medium weight (500) - The quick brown fox</Text>
          <Text weight="semibold">Semibold weight (600) - The quick brown fox</Text>
          <Text weight="bold">Bold weight (700) - The quick brown fox</Text>
        </CardBody>
      </Card>

      {/* Semantic Colors */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Semantic Colors</Subtitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Body>Foreground (primary text color) - WCAG AA ✓</Body>
          <Body className="before:content-['●'] before:text-blue-600 before:mr-2">Primary color - Use blue-700/blue-300 for text</Body>
          <Body className="before:content-['●'] before:text-purple-600 before:mr-2">Secondary color - Use purple-700/purple-300 for text</Body>
          <Body className="before:content-['●'] before:text-green-600 before:mr-2">Success color - Use green-700/green-300 for text</Body>
          <Body className="before:content-['●'] before:text-amber-600 before:mr-2">Warning color - Use amber-700/amber-300 for text</Body>
          <Body className="before:content-['●'] before:text-red-600 before:mr-2">Danger color - Use red-700/red-300 for text</Body>
          <SecondaryText>Default-700 (muted text) - WCAG AA ✓</SecondaryText>
          <Caption>Caption text (default-800) - WCAG AA ✓</Caption>
        </CardBody>
      </Card>

      {/* Links */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Links</Subtitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div>
            <Caption className="mb-2">Internal Link</Caption>
            <Link href="/profile">View your profile</Link>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">External Link (auto-adds accessibility attributes)</Caption>
            <Link href="https://example.com" external>
              Visit external site
            </Link>
          </div>
        </CardBody>
      </Card>

      {/* Form Elements */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Form Labels</Subtitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label>Standard Label</Label>
            <Caption className="mt-1">Helper text goes here</Caption>
          </div>
          <div>
            <Label required>Required Field</Label>
            <Caption className="mt-1 text-danger">This field is required</Caption>
          </div>
        </CardBody>
      </Card>

      {/* Code & Quotes */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Code & Quotes</Subtitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Caption className="mb-2">Inline Code</Caption>
            <Body>
              Run <Code>npm install</Code> to install dependencies.
            </Body>
          </div>
          <Divider />
          <div>
            <Caption className="mb-2">Blockquote</Caption>
            <Blockquote>
              This is a quoted section from another source. It has a left border
              and italic styling to distinguish it from regular text.
            </Blockquote>
          </div>
        </CardBody>
      </Card>

      {/* Real-World Example */}
      <Card className="mb-6">
        <CardHeader>
          <Subtitle>Real-World Example: Video Card</Subtitle>
        </CardHeader>
        <CardBody>
          <SectionTitle>Amazing Colorado Road Trip</SectionTitle>
          <div className="flex items-center gap-2 mt-2 mb-4">
            <Caption>Posted by John Doe</Caption>
            <Caption>•</Caption>
            <Caption>2 days ago</Caption>
            <Caption>•</Caption>
            <Caption>1,234 views</Caption>
          </div>
          <Body className="mb-3">
            This is the video description that provides context about what viewers
            will see. It uses the primary body text style for maximum readability.
          </Body>
          <SecondaryText className="mb-2">
            Location: Denver, Colorado
          </SecondaryText>
          <div className="flex gap-4 mt-4">
            <Link href="/video/123">Watch Video</Link>
            <Link href="/profile/johndoe">View Profile</Link>
          </div>
        </CardBody>
      </Card>

      {/* Dark Mode Preview */}
      <Card className="mb-6 bg-content1 dark:bg-content1">
        <CardHeader>
          <Subtitle>Dark Mode Support</Subtitle>
        </CardHeader>
        <CardBody>
          <Body className="mb-4">
            All typography components automatically adapt to dark mode. Toggle your
            system theme to see the difference.
          </Body>
          <div className="p-4 rounded-lg bg-content2 dark:bg-content2">
            <Text variant="body" className="mb-2">Primary text in both modes</Text>
            <Text variant="secondary" className="mb-2">Secondary text in both modes</Text>
            <Text variant="caption">Caption text in both modes</Text>
          </div>
        </CardBody>
      </Card>

      {/* Accessibility Notes */}
      <Card className="mb-6 border-2 border-green-600">
        <CardHeader>
          <SectionTitle>♿ Accessibility Features</SectionTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          <Body>✅ All text meets WCAG AA contrast requirements (4.5:1 for normal, 3:1 for large)</Body>
          <Body>✅ Semantic HTML elements for screen readers</Body>
          <Body>✅ Proper heading hierarchy (h1 → h2 → h3)</Body>
          <Body>✅ External links announce "opens in new tab"</Body>
          <Body>✅ Dark mode support with maintained contrast</Body>
          <Body>✅ Responsive typography for all screen sizes</Body>
        </CardBody>
      </Card>
    </main>
  );
};

export default TypographyExample;
